// Library Imports
import { RootState } from 'store';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// Local Imports
import { ICampaignDropdownItem } from '../interfaces';
import SelectFilterWithIcon from './SelectFilterWithIcon';
import { ANALYTICS_DASHBOARD_SET_SELECTED_CAMPAINGS } from 'constants/types';
import {
  reorderCampaignsAndEmails,
  resetSelectedCampaigns,
  searchAnalyticsDropdownCampaigns,
  selectCampaign,
  setCampaignsFilter,
  setShareProfileFilter,
  toggleWorkflowFilter,
  setDefaultWorkflow,
} from '../Dashboard/actions';
import {
  getAnalyticsFilterId,
  getQueryAllCampaigns,
  getQueryCampaignId,
  getQueryEmailId,
  getQueryIsAIEmail,
  getQueryWorkflowId,
} from 'modules/EmailBrowser/utils';
// Icon Imports
import AllCampaignIcon from '../../../assets/img/icons/Campaign/AllCampaign.svg';
interface ICampaignFiltersDropdown {
  dropdownClass?: string;
  closeDropDownHandler: () => void;
}

const CampaignFiltersDropdown: React.FC<ICampaignFiltersDropdown> = (props) => {
  // Props
  const { dropdownClass, closeDropDownHandler } = props;

  // Redux States
  const workflows = useSelector((state: RootState) => state.analyticsDashboard.workflows);
  const campaigns = useSelector((state: RootState) => state.analyticsDashboard.campaigns);
  const hasDailyIqPermission = useSelector((state: RootState) => state.user.has_daily_iq_perm);
  const isCampaignSearch = useSelector((state: RootState) => state.analyticsDashboard.isCampaignSearch);
  const campaignsFilters = useSelector((state: RootState) => state.analyticsDashboard.campaignsFilters);
  const isDefaultDataSet = useSelector((state: RootState) => state.analyticsDashboard.isDefaultDataSet);
  const hasShareProfilePermission = useSelector((state: RootState) => state.user.has_share_profile_perm);
  const filteredCampaigns = useSelector((state: RootState) => state.analyticsDashboard.filteredCampaigns);
  const searchedCampaigns = useSelector((state: RootState) => state.analyticsDashboard.searchedCampaigns);
  const firstAllDataShown = useSelector((state: RootState) => state.analyticsDashboard.firstAllDataShown);
  const defaultCampaigns = useSelector((state: RootState) => state.analyticsGraph.data?.campaigns_series);
  const campaignsLoading = useSelector((state: RootState) => state.analyticsDashboard.campaignsAssetsLoading);
  const selectedCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.selectedCampaignsIds);
  const campaignsLimitReached = useSelector((state: RootState) => state.analyticsDashboard.campaignsLimitReached);
  const hasWeeklyAccountInsightsPermission = useSelector((state: RootState) => state.user.has_weekly_account_insight_perm);
  const startDate = useSelector((state: RootState) => state.analyticsGraph.startDate);
  const endDate = useSelector((state: RootState) => state.analyticsGraph.endDate);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights, isWorkflow } = useSelector((state: RootState) => state.analyticsDashboard);

  // Local States
  const [alreadyUsedQueryWfId, setAlreadyUsedQueryWfId] = useState(false);

  // Local variables
  const dispatch = useDispatch();
  const fsId = getAnalyticsFilterId();
  const queryWorkflowId = getQueryWorkflowId();

  // useEffects
  useEffect(() => {
    return () => {
      resetSelectedCampaigns(true);
      searchAnalyticsDropdownCampaigns('');
    };
  }, []);

  useEffect(() => {
    // hook to handle the default state of select/unselect
    if (campaigns.length && defaultCampaigns?.length && !isDefaultDataSet && firstAllDataShown && !queryWorkflowId && !isWorkflow && !fsId) {
      dispatchActionToSetDefault();
    }
  }, [defaultCampaigns?.length, campaigns?.length, firstAllDataShown]);

  useEffect(() => {
    // hook to handle the default state of select/unselect
    if (!alreadyUsedQueryWfId && queryWorkflowId && workflows.length && campaigns.length && !isDefaultDataSet && !fsId) {
      setDefaultWorkflow(queryWorkflowId);
      setAlreadyUsedQueryWfId(true);
    }
  }, [queryWorkflowId, campaigns, workflows]);

  // Component Logic
  function dispatchActionToSetDefault() {
    const campaignId = getQueryCampaignId();
    const isAIEmail = getQueryIsAIEmail();
    const queryEmailId = getQueryEmailId();
    const isAllCampaignsQuery = getQueryAllCampaigns();
    let tempStartDate, tempEndDate;
    const filterFromLocalStorage = JSON.parse(localStorage.getItem('analyticsFiltersWithId') || '{}');
    if (fsId && filterFromLocalStorage && filterFromLocalStorage.id == fsId) {
      tempStartDate = filterFromLocalStorage.saved_data.start_date;
      tempEndDate = filterFromLocalStorage.saved_data.end_date;
    } else {
      tempStartDate = startDate;
      tempEndDate = endDate;
    }
    if (!isAIEmail && isAllCampaignsQuery === 'false' && !campaignId) {
      // if url have not campaign id it will select default campaigns
      dispatch({
        type: ANALYTICS_DASHBOARD_SET_SELECTED_CAMPAINGS.SET_DEFAULT,
        payload: {
          campaignIds: campaignId ? [campaignId] : defaultCampaigns?.map((campaign) => campaign.campaign_id),
          emailId: queryEmailId,
          date: { startDate: tempStartDate, endDate: tempEndDate },
        },
      });
    }
  }

  return (
    <SelectFilterWithIcon
      label={selectedCampaignsIds.length > 0 ? `${selectedCampaignsIds.length} Campaign(s) selected` : 'Campaigns'}
      icon={AllCampaignIcon}
      searchTitle="Search campaigns…"
      filterTitle="Filter Campaign"
      idPrefix="campaigns-01"
      items={isCampaignSearch ? searchedCampaigns : filteredCampaigns.length > 0 ? filteredCampaigns : campaigns}
      onSearchInput={(value: any) => {
        searchAnalyticsDropdownCampaigns(value);
      }}
      filters={campaignsFilters}
      setCheckBox={(value: boolean) => setCampaignsFilter(value)}
      setShareProfileFilter={(value: boolean) => setShareProfileFilter(value)}
      setWorkflowFilter={toggleWorkflowFilter}
      setSelected={(value: ICampaignDropdownItem) => selectCampaign(value.id, selectedCampaignsIds)}
      selectedItems={selectedCampaignsIds}
      campaignSelectedMessage={campaignsLimitReached}
      dropdownClass={`${dropdownClass} dropdown-menu-wrapper-v2`}
      dropdownType="Campaigns"
      onClose={() => {
        reorderCampaignsAndEmails();
        closeDropDownHandler();
      }}
      isShareProfile={isShareProfile}
      isDailyIQ={isDailyIQ}
      isWorkflow={isWorkflow}
      disableDigestEmails={isWorkflow}
      disableAdhocEmails={isWorkflow}
      disableRows={isWorkflow}
      hideDailyIQ={!hasDailyIqPermission}
      isWeeklyAccountInsights={isWeeklyAccountInsights}
      hideWeeklyAccountInsights={!hasWeeklyAccountInsightsPermission}
      hideShareProfile={!hasShareProfilePermission}
      hideWorkflow={false}
      disableDropdown={!firstAllDataShown}
      loading={campaignsLoading}
    />
  );
};

export default React.memo(CampaignFiltersDropdown);
